import { MeshPhysicalMaterial, MeshStandardMaterial, Texture } from 'three';

export interface MaterialUpdateConfig {
	materials: Map<string, MeshStandardMaterial | MeshPhysicalMaterial>;
	envMap: Texture;
	batchSize?: number; // Materials per frame (default: 5)
	onProgress?: (completed: number, total: number) => void;
	onComplete?: () => void;
}

/**
 * IncrementalMaterialUpdater spreads material updates across multiple frames
 * to prevent UI blocking when updating many materials at once.
 */
export class IncrementalMaterialUpdater {
	#isUpdating = false;
	#cancelCurrentUpdate = false;

	/**
	 * Check if an update is currently in progress
	 */
	get isUpdating(): boolean {
		return this.#isUpdating;
	}

	/**
	 * Cancel any in-progress update
	 */
	cancel(): void {
		this.#cancelCurrentUpdate = true;
	}

	/**
	 * Update materials in batches across frames.
	 * Returns a promise that resolves when all materials are updated.
	 */
	updateMaterials(config: MaterialUpdateConfig): Promise<void> {
		return new Promise((resolve) => {
			const { materials, envMap, batchSize = 5, onProgress, onComplete } = config;

			const materialArray = Array.from(materials.values());
			const total = materialArray.length;

			if (total === 0) {
				onProgress?.(0, 0);
				onComplete?.();
				resolve();
				return;
			}

			let index = 0;
			this.#isUpdating = true;
			this.#cancelCurrentUpdate = false;

			const processBatch = () => {
				// Check if cancelled
				if (this.#cancelCurrentUpdate) {
					this.#isUpdating = false;
					this.#cancelCurrentUpdate = false;
					resolve();
					return;
				}

				const endIndex = Math.min(index + batchSize, total);

				// Update batch of materials
				for (let i = index; i < endIndex; i++) {
					const material = materialArray[i];
					material.envMap = envMap;
					material.needsUpdate = true;
				}

				index = endIndex;
				onProgress?.(index, total);

				if (index < total) {
					// More materials to process - schedule next batch
					requestAnimationFrame(processBatch);
				} else {
					// All done
					this.#isUpdating = false;
					onComplete?.();
					resolve();
				}
			};

			// Start processing on next frame
			requestAnimationFrame(processBatch);
		});
	}

	/**
	 * Update materials synchronously (fallback for when blocking is acceptable)
	 */
	updateMaterialsSync(
		materials: Map<string, MeshStandardMaterial | MeshPhysicalMaterial>,
		envMap: Texture
	): void {
		for (const material of materials.values()) {
			material.envMap = envMap;
			material.needsUpdate = true;
		}
	}
}
